<template>
  <section>
    <PageTitle>
      <span v-html="$store.state.app.pageName" />
    </PageTitle>
    <PageContent>
      <form @submit.prevent="search">
        <Container>
          <NoticeBox type="error" v-if="hasSearchError">
            Please enter the venue and duration, or customer number
          </NoticeBox>
          <div class="form-group">
            <label>Venue 利用店舗</label>
            <div class="select is-fullwidth">
              <select
                class="form-control"
                v-model.number="query.use_facility_id"
                @change="onFacilityChange"
              >
                <option value="0">Select a venue</option>
                <option
                  v-for="item in facilities"
                  :key="item.use_facility_id"
                  :value="item.use_facility_id"
                  v-html="item.use_facility_name"
                />
              </select>
            </div>
            <div
              v-if="query.use_facility_id !== 0"
              class="select space-t-2 is-fullwidth"
            >
              <select
                class="form-control"
                v-model.number="query.use_child_facility_id"
                :disabled="isFacilityLoading"
              >
                <option value="0">Select a place</option>
                <option
                  v-for="item in childFacilities"
                  :key="item.use_child_facility_id"
                  :value="item.use_child_facility_id"
                  v-html="item.use_child_facility_name"
                />
              </select>
            </div>
            <div class="space-t-2">
              <button
                class="button small is-secondary is-outlined has-icon"
                type="button"
                @click="saveQuery"
                :disabled="isSaveQueryLoading"
              >
                <i class="fas fa-download text-primary"></i>
                <span class="text-bold">Save the selected venue 店舗を保存</span>
              </button>
            </div>
          </div>
          <div class="form-group">
            <label>Duration 期間</label>
            <div class="vertical-center">
              <Datepicker v-model="query.start_date" />
              <span class="space-x-1">〜</span>
              <Datepicker position="right" v-model="query.end_date" />
            </div>
          </div>
          <div class="form-group">
            <label>Customer Number 会員番号</label>
            <input type="text" class="form-control" v-model="query.cmc_no" />
          </div>
          <div class="form-footer">
            <div class="text-center">
              <WaveButton type="submit" class="button" :loading="loading">
                Search
              </WaveButton>
            </div>
          </div>
        </Container>
      </form>

      <div
        id="results"
        :class="{ 'space-t-6': isNoResult || results.length > 0 }"
      >
        <template v-if="isNoResult">
          <Container>
            <NoticeBox type="error" :borderless="true" class="space-b-2">
              No results found
            </NoticeBox>
            <p class="text-xs">Please try another search</p>
          </Container>
        </template>
        <template v-if="results.length > 0">
          <Container>
            <p class="text-bold text-xs space-b-1">
              Name / Amount / Type of visit / Date / Venue / Place / Purpose / Number of visitors
            </p>
            <div class="link-group is-narrow is-multiline">
              <router-link
                :to="result | visitLink"
                v-for="result in results"
                :key="result.use_history_id"
              >
                <div class="use-history">
                  <p class="use-history-name">
                    Mr./Ms. {{ result.customer_name }}
                  </p>
                  <div class="use-history-footer">
                    <p class="use-history-bill"
                      v-if="result.bill_amount != null">
                      {{ result.bill_amount | numFormat}}
                    </p>
                    <p class="use-history-bill" v-else>
                      {{ emptyText }}
                    </p>
                    <div class="use-history-detail vertical-align">
                      <span
                        class="use-history-type space-r-1"
                        :class="result.history_type | historyTypeClassName"
                      >
                        {{ getHistoryTypeName(result.history_type) }}
                      </span>
                      <time class="space-r-1">
                        {{ result.use_datetime | formatDatetime }}
                      </time>
                      <br class="mobile-only" />
                      <span class="space-r-1"
                        v-html="result.use_child_facility_name" />
                      <span class="space-r-1"
                        v-html="result.use_facility_name" />
                      <span class="space-r-1"
                        v-html="result.use_purpose_name || emptyText" />
                      <span v-html="result.count_of_people || emptyText" />
                    </div>
                  </div>
                </div>
              </router-link>
            </div>
            <p
              v-if="isMaxResultsExceeded"
              class="space-t-2"
            >{{ maxResultsMessage }}</p>
          </Container>
        </template>
      </div>

    </PageContent>

    <Modal
      id="save-history"
      title="The selected venue is saved"
      icon="check"
      @ok="$modal.close('save-history')"
    >登録された条件は、利用履歴検索にデフォルトで表示されます</Modal>
  </section>
</template>

<script>
import moment from 'moment'
import Modal from '@/components/Modal.vue'
import Datepicker from '@/components/Datepicker.vue'
import NoticeBox from '@/components/NoticeBox.vue'
import api from '@/api/use-history'
import facilityApi from '@/api/use-facility'
import receptionistApi from '@/api/receptionist'
import numFormat from '@/filters/num-format'
import { renderError, isNotFound } from '@/utils/error'
import { linkScroll } from '@/utils/dom'

const HISTORY_TYPES = [
  { value: '1', label: 'Visit' },
  { value: '2', label: 'Voucher' }
]

const EMPTY_TEXT = '-'

export default {
  components: {
    Modal,
    NoticeBox,
    Datepicker
  },
  metaInfo() {
    return {
      title: this.$store.state.app.pageName,
      bodyAttrs: { class: 'use-history' }
    }
  },
  filters: {
    numFormat,
    formatDatetime(val) {
      return moment(val).format('YYYY/MM/DD kk:mm:ss')
    },
    visitLink(val) {
      return {
        name: 'visit.show',
        params: {
          visitorId: val.customer_id,
          historyId: val.use_history_id
        }
      }
    },
    historyTypeClassName(val) {
      return {
        'is-shop': val === 1,
        'is-ticket': val === 2
      }
    }
  },
  data() {
    return {
      facilities: [],
      childFacilities: [],
      historyTypes: HISTORY_TYPES,
      emptyText: EMPTY_TEXT,
      conditionRevision: 0,
      query: {
        use_facility_id: 0,
        use_child_facility_id: 0,
        start_date: '',
        end_date: '',
        cmc_no: ''
      },
      results: [],
      isNoResult: false,
      hasSearchError: false,
      isSearchClicked: false,
      loading: false,
      maxResults: 500,
      isSaveQueryLoading: false,
      isFacilityLoading: false
    }
  },
  computed: {
    maxResultsMessage() {
      return `Latest ${this.maxResults} visits are shown.`
    },
    isMaxResultsExceeded() {
      return this.results.length === this.maxResults
    }
  },
  methods: {
    /**
     * 初期表示
     */
    init() {
      // 期間のデフォルト値を設定
      this.setDefaultDate()

      this.$withLoading(async () => {
        // 会場と施設のマスタを取得
        await this.initFacility()

        if (
          this.$route.query.q === 'last' &&
          !this.$store.getters['useHistory/isEmpty']
        ) {
          const { query: storedQuery } = this.$store.state.useHistory
          this.query = { ...storedQuery } // 参照コピーを回避

          await this.onFacilityChange()
          this.query.use_child_facility_id = storedQuery.use_child_facility_id

          return this.search()
        }

        this.$store.commit('useHistory/CLEAR_QUERY')

        await this.restoreQuery()

        if (this.$route.query.u) {
          this.query.cmc_no = this.$route.query.u
          this.query.start_date = ''
          this.query.end_date = ''
          this.search()
        }
      })
    },
    /**
     * 利用施設を初期化
     */
    async initFacility() {
      try {
        this.facilities = await facilityApi.get()
      } catch(err) {
        renderError(err)
      }
    },
    /**
     * 会場（親）が更新されたとき施設（子）選択肢を入れ替える
     */
    async onFacilityChange() {
      if (this.isFacilityLoading) {
        return false
      }

      this.isFacilityLoading = true

      this.query.use_child_facility_id = 0
      const { use_facility_id } = this.query

      if (use_facility_id === 0) {
        this.isFacilityLoading = false
        return false
      }

      try {
        this.childFacilities = await facilityApi.getChildren(use_facility_id)
        this.$nextTick(() => (this.isFacilityLoading = false))
      } catch(err) {
        this.isFacilityLoading = false
        // 無効な施設
        if (isNotFound(err)) {
          this.clearFacility()
          return false
        }
        renderError(err)
      }
    },
    /**
     * 種別を初期化
     */
    initHistoryType() {
      // 種別のデフォルト値は両方にチェック
      this.query.history_type = HISTORY_TYPES.map(item => item.value)
    },
    /**
     * 期間を初期化
     */
    setDefaultDate() {
      // デフォルト値は本日〜本日
      const today = moment().format('YYYY-MM-DD')
      this.query.start_date = today
      this.query.end_date = today
    },
    clearFacility() {
      this.query.use_facility_id = 0
      this.query.use_child_facility_id = 0
    },
    isFacilityInvalid(id) {
      return !this.facilities.some(f => f.use_facility_id === id)
    },
    /**
     * 検索条件を復元する
     */
    async restoreQuery() {
      try {
        const response = await receptionistApi.getCondition()

        // 無効な店舗
        if (this.isFacilityInvalid(response.use_facility_id)) {
          this.clearFacility()
          return false
        }

        this.query.use_facility_id = response.use_facility_id
        this.conditionRevision = response.revision

        await this.onFacilityChange()

        this.query.use_child_facility_id = response.use_child_facility_id
      } catch(err) {
        renderError(err)
      }
    },
    /**
     * 検索条件保存
     */
    async saveQuery() {
      if (this.isSaveQueryLoading) {
        return false
      }

      this.isSaveQueryLoading = true

      const { use_facility_id, use_child_facility_id } = this.query

      try {
        const response = await receptionistApi.saveCondition({
          use_facility_id,
          use_child_facility_id,
          revision: this.conditionRevision
        })

        this.conditionRevision = response.revision

        this.$modal.show('save-history')
      } catch(err) {
        renderError(err)
      } finally {
        this.isSaveQueryLoading = false
      }
    },
    getHistoryTypeName(value) {
      const type = HISTORY_TYPES.filter(t => t.value * 1 === value * 1).pop()
      return type ? type.label : ''
    },
    /**
     * 検索チェック
     * @returns {boolean} 検索できるか？
     */
    validate() {
      // 会員番号が空 かつ 利用施設または期間が未入力 → NG
      // 例えば、会員番号が入っていれば、利用施設または期間が未入力でもOK（はいっててもOK）
      // 利用施設と期間が両方入力されていれば、会員番号が空でもOK

      if (this.query.cmc_no !== '') {
        return true
      }

      return (
        this.query.use_facility_id !== 0 &&
        this.query.use_child_facility_id !== 0 &&
        this.query.start_date !== '' &&
        this.query.end_date !== ''
      )
    },
    async search() {
      this.isNoResult = false
      this.hasSearchError = false
      this.results = []

      if (!this.validate()) {
        this.hasSearchError = true
        window.scrollTo(0, 0)
        return false
      }

      this.loading = true

      try {
        this.$store.commit('useHistory/SET_QUERY', this.query)

        this.results = await api.get(this.query)

        if (this.results.length === 0) {
          this.isNoResult = true
        }

        // 結果表示まで自動スクロール
        this.$nextTick(() => linkScroll('#results'))
      } catch(err) {
        renderError(err)
      } finally {
        this.loading = false
      }
    }
  },
  created() {
    this.init()
  }
}
</script>
